import type { DeckorateNav } from "../../components/deckorateHome/ribbonGroups";
import type { ActionChipId } from "./types";

/**
 * A Deckorate screen a chip can open.
 *
 * `asset-library` is excluded deliberately: it is the one {@link DeckorateNav}
 * value that isn't a routed view (DeckorateModule opens it as a dialog overlay),
 * and no chip targets it — so keeping it out of this type makes an accidental
 * mapping a compile error rather than a silent no-op.
 */
export type ChipTargetView = Exclude<DeckorateNav, "asset-library">;

/**
 * Chip → the Deckorate feature that implements it.
 *
 * Deliberately partial. A chip is listed here only when the feature behind it
 * actually exists and works today; every other chip stays a placeholder and
 * reports "not available yet" on click (see `useActionChipDispatch`). That makes
 * this map the single, checkable answer to "which pills are live?" — wiring a
 * new feature up is one entry here, and nothing else.
 *
 * Not yet listed, because no implementation exists to point at:
 * - `transformSlide`, `improvements` — AI slide-level actions.
 * - `improveWording`, `makeConcise`, `expandText`, `changeTone` — AI text actions.
 * - `addIcon` — needs the icon library.
 * - `tableToChart`, `summarizeTable`, `findAnomalies` — table actions.
 * - `imageToTable`, `vectorize`, `removeBackground`, `addCaption` — image
 *   actions, all requiring AI vision.
 *
 * `imageToChart` is absent for a different reason: it is implemented, but as
 * an in-pane SHEET rather than a routed view — see {@link CHIP_SHEETS}.
 */
export const CHIP_TARGET_VIEWS: Readonly<Partial<Record<ActionChipId, ChipTargetView>>> = {
  createSlides: "create-slide",
  fixAlignment: "fix-alignment",
  infographic: "infographic",
  getInsights: "insights",
  photoHead: "photo-headshot",
  sketchToSlide: "sketch-to-slide",
  speakerNotes: "speaker-notes",
};

/**
 * The screen a chip opens, or `null` while it is still a placeholder.
 *
 * Selection is *not* consulted: the document selection is untouched by moving
 * between task-pane tabs, so each panel re-reads it on arrival and applies its
 * own guards (e.g. Fix alignment needs 2+ shapes for "selected" scope). Gating
 * here as well would only risk disagreeing with the panel that does the work.
 */
export function chipTargetView(id: ActionChipId): ChipTargetView | null {
  // Checked for being a string, not merely present: a plain object inherits from
  // Object.prototype, so an id like "toString" or "constructor" resolves to an
  // inherited *function* and a bare `?? null` would let it through as an
  // implemented chip. `id` is typed, but it ends up in a navigation call, so this
  // stays cheap insurance against a value arriving from outside the type system.
  const view = CHIP_TARGET_VIEWS[id];
  return typeof view === "string" ? view : null;
}

/**
 * An in-pane sheet a chip can open.
 *
 * A sheet is not a screen: it docks at the bottom of the CHAT tab, above the
 * input bar, leaving the thread visible behind it (see
 * shared/styles/inPaneSheet.module.css). Actions that operate on the current
 * selection and hand back a single result belong here rather than in a routed
 * view, because switching tabs to run them would take the user away from the
 * chat they started in.
 */
export type ChipSheetId = "convert-chart";

/**
 * Chip → the in-pane sheet that implements it.
 *
 * Partial and checked exactly like {@link CHIP_TARGET_VIEWS}. A chip must not
 * appear in both maps; `chipSheet` is consulted first, so an entry here wins.
 */
export const CHIP_SHEETS: Readonly<Partial<Record<ActionChipId, ChipSheetId>>> = {
  imageToChart: "convert-chart",
};

/** The sheet a chip opens, or `null` when it opens none. Narrowed for the
 *  same prototype-pollution reason as {@link chipTargetView}. */
export function chipSheet(id: ActionChipId): ChipSheetId | null {
  const sheet = CHIP_SHEETS[id];
  return typeof sheet === "string" ? sheet : null;
}

/** True when the chip has a working feature behind it — a routed view or a sheet. */
export function isChipImplemented(id: ActionChipId): boolean {
  return chipTargetView(id) !== null || chipSheet(id) !== null;
}
